import { createStudentApi } from '@e-learning/core';
import type { StudentFeedCourseItem } from '@e-learning/core';
import type { VideoProgressCardStatus } from '@e-learning/shared';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../../api/client';

const studentApi = createStudentApi(api);

export type StudentFeedSectionKind = 'continuing' | 'watchAgain' | 'available';

export interface UseStudentFeedResult {
  continuing: StudentFeedCourseItem[];
  watchAgain: StudentFeedCourseItem[];
  available: StudentFeedCourseItem[];
  loading: boolean;
  error: { key: 'student_dashboard.feed.load_error' } | null;
  refetch: () => void;
}

/** Mapeia item do feed para props do {@link VideoProgressCard}. */
export function mapFeedCourseToVideoCardProps(
  course: StudentFeedCourseItem,
  section: StudentFeedSectionKind
): {
  coverImageUrl: string;
  coverImageAlt: string;
  xp: number;
  progressPercent: number;
  showProgress?: boolean;
  showXp?: boolean;
  leftTopBadgeLabel?: string;
  title: string;
  description: string;
  status: VideoProgressCardStatus;
  completedAt?: string;
  actionButtonVariant?: 'default' | 'outline_green';
} {
  const base = {
    coverImageUrl: course.coverImageUrl ?? '',
    coverImageAlt: course.title,
    xp: course.totalPoints,
    title: course.title,
    description: course.description,
  };
  switch (section) {
    case 'continuing':
      // A API retorna o percentual de progresso para cursos em andamento.
      // Mantemos fallback para `porcentagem` (nome anterior do campo).
      return {
        ...base,
        progressPercent: course.progressPercentage ?? course.porcentagem ?? 0,
        status: 'in_progress',
      };
    case 'watchAgain':
      return {
        ...base,
        progressPercent: 100,
        showProgress: false,
        showXp: false,
        status: 'completed',
        completedAt: course.completedAt ?? undefined,
        actionButtonVariant: 'outline_green',
      };
    case 'available':
      return {
        ...base,
        progressPercent: 0,
        showProgress: false,
        leftTopBadgeLabel: course.categoryName,
        status: 'not_started',
      };
  }
}

/**
 * Carrega o feed do aluno (GET v1/student/feed) para os carrosséis do dashboard.
 */
export function useStudentFeed(search?: string): UseStudentFeedResult {
  const { i18n } = useTranslation();
  const [continuing, setContinuing] = useState<StudentFeedCourseItem[]>([]);
  const [watchAgain, setWatchAgain] = useState<StudentFeedCourseItem[]>([]);
  const [available, setAvailable] = useState<StudentFeedCourseItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<
    { key: 'student_dashboard.feed.load_error' } | null
  >(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    studentApi
      .fetchFeed()
      .then(data => {
        if (cancelled) return;
        setContinuing(
          Array.isArray(data.continuing) ? data.continuing : []
        );
        setWatchAgain(Array.isArray(data.watchAgain) ? data.watchAgain : []);
        setAvailable(Array.isArray(data.available) ? data.available : []);
      })
      .catch(() => {
        if (cancelled) return;
        setError({ key: 'student_dashboard.feed.load_error' });
        setContinuing([]);
        setWatchAgain([]);
        setAvailable([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tick, search, i18n.language]);

  const refetch = useCallback(() => setTick(n => n + 1), []);

  return {
    continuing,
    watchAgain,
    available,
    loading,
    error,
    refetch,
  };
}
